import {
  getOrderProductsSchema,
  createOrderProductsSchema,
  orderProductsUpdateSchema,
} from '@messenger/types'
import type {
  GetOrderProductNormalized,
  GetM3OrderProductNormalized,
  GetCreatedOrderProductNormalized,
  ApplicationType,
} from '@messenger/types'
import axios from '@/api/axios.config'
import { z } from 'zod'

export const getOrderProducts = async (
  orderId: z.infer<typeof getOrderProductsSchema>['orderId'],
  caseId: z.infer<typeof getOrderProductsSchema>['caseId'],
  applicationType: z.infer<typeof getOrderProductsSchema>['applicationType'],
) => {
  const params = {
    order_id: orderId,
    case_id: caseId,
    application_type: applicationType,
  }
  return await axios.get<
    GetOrderProductNormalized[] | GetM3OrderProductNormalized[]
  >(`orderProducts`, { params })
}

export const createOrderProduct = async (
  payload: z.infer<typeof createOrderProductsSchema>,
) => {
  return await axios.post<GetCreatedOrderProductNormalized>(
    `createOrderProducts`,
    payload,
  )
}

export const updateOrderProduct = async (
  orderProductId: string,
  payload: z.infer<typeof orderProductsUpdateSchema>,
  applicationType: ApplicationType,
) => {
  return await axios.patch<
    GetOrderProductNormalized | GetM3OrderProductNormalized
  >(`orderProducts/${orderProductId}`, {
    ...payload,
    application_type: applicationType,
  })
}

// Admin-only. Deletes all OrderPageSets for the given orderProduct so the
// next editor load recreates them from the template. Used by the preview
// screen's "Reset Layout" recovery button when an OPS is in a bad state.
export const resetOrderProduct = async (
  orderProductId: string,
  applicationType: ApplicationType,
) => {
  return await axios.post(`orderProducts/${orderProductId}/reset`, {
    application_type: applicationType,
  })
}

/**
 * One version of the line as it stands, taken before an editor Save writes
 * anything. The answer is the version number the Save's page writes should
 * name, or null when no version could be taken — a Save is never failed for
 * versioning's sake, so the caller falls back to a version per page write.
 *
 * The timeout outlives the snapshot's own budget on the server (a 5s wait for
 * the line lock plus a 15s transaction), so a call that expires here is one
 * the server was never going to answer in time, not one it is about to.
 */
export const EDITOR_SAVE_VERSION_TIMEOUT_MS = 25_000

/**
 * The one reason the route admits besides its default, an editor Save: the
 * version taken before an admin turns a TinyMCE text box into a Lotus text
 * box, the server's `SNAPSHOT_REASONS.TEXT_BOX_CONVERSION`.
 */
export const EDITOR_TEXT_BOX_CONVERSION_VERSION_REASON = 'text_box_conversion'

export type EditorSaveVersionReason =
  typeof EDITOR_TEXT_BOX_CONVERSION_VERSION_REASON

export const createEditorSaveVersion = async (
  orderProductId: string,
  applicationType: ApplicationType,
  reason: EditorSaveVersionReason | undefined,
) => {
  return await axios.post<{ version_number: number | null }>(
    `orderProducts/${orderProductId}/versions/editor-save`,
    {
      application_type: applicationType,
      ...(reason !== undefined && { reason }),
    },
    { timeout: EDITOR_SAVE_VERSION_TIMEOUT_MS },
  )
}
